import { useState, useEffect } from 'react'
import { useGameStore, Building } from '@store/gameStore'
import { GameLayout } from '@components/GameLayout'
import { AdModal } from '@components/AdModal'
import './TutorialScreen.css'

export function TutorialScreen() {
    const [step, setStep] = useState(0)
    const [showAd, setShowAd] = useState(false)
    const [showReward, setShowReward] = useState(false)
    const [rewardData, setRewardData] = useState<{ gems?: number; gold?: number; power?: number } | null>(null)
    const [upgradeTimer, setUpgradeTimer] = useState<{ [key: string]: number }>({})
    const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(null)

    // Store
    const completeTutorial = useGameStore(state => state.completeTutorial)
    const addGems = useGameStore(state => state.addGems)
    const addGold = useGameStore(state => state.addGold)
    const buildings = useGameStore(state => state.buildings)
    const setScreen = useGameStore(state => state.setScreen)
    const setTutorialPhase = useGameStore(state => state.setTutorialPhase)
    const tutorialPhase = useGameStore(state => state.tutorialPhase)
    const username = useGameStore(state => state.username)
    const power = useGameStore(state => state.power)
    const equipment = useGameStore(state => state.equipment) || {}
    const empireColor = useGameStore(state => state.empireColor)
    const empireEmblem = useGameStore(state => state.empireEmblem)

    // Calculate total power including equipment
    const equipmentPower = Object.values(equipment).reduce(
        (sum, item) => sum + (item?.power || 0),
        0
    )
    const totalPower = power + equipmentPower

    // Tutorial Steps Configuration
    const steps = [
        {
            title: "Willkommen!",
            text: `Hallo ${username}! Dein Imperium ist gegründet. Aber dein Rathaus sieht etwas... schwach aus. Lass es uns aufwerten!`,
            highlight: "townhall",
            button: null
        },
        {
            title: "Echtzeit-Stau",
            text: "5 Minuten warten? In der Zeit hast du schon vergessen, warum du spielst. Nutze den Gratis-Skip für das Tutorial!",
            highlight: "townhall",
            skipFree: true,
            button: null
        },
        {
            title: "Geschafft!",
            text: "Du hast gelernt: Warten ist für Verlierer. Hier ist dein Start-Guthaben, um richtig loszulegen!",
            button: "Belohnung einsammeln",
            highlight: null,
            reward: { gems: 50, gold: 500 }
        }
    ]

    const currentStep = steps[step] || steps[0]

    // Timer Effect
    useEffect(() => {
        const interval = setInterval(() => {
            setUpgradeTimer(prev => {
                const next = { ...prev }
                let changed = false
                Object.keys(next).forEach(key => {
                    if (next[key] > 0) {
                        next[key] -= 1
                        changed = true
                        if (next[key] === 0) {
                            delete next[key]
                            if (step === 1) advanceStep()
                        }
                    }
                })
                return changed ? next : prev
            })
        }, 1000)
        return () => clearInterval(interval)
    }, [step])

    const advanceStep = () => {
        if (step < steps.length - 1) {
            setStep(s => s + 1)
        } else {
            // Building phase complete → move to Shop phase
            setTutorialPhase('shop')
            setScreen('shop')
        }
    }

    const handleBuildingClick = (building: Building) => {
        if (currentStep.highlight === building.id && !upgradeTimer[building.id]) {
            setSelectedBuilding(building)
        }
    }

    const handleFreeSkip = () => {
        setUpgradeTimer({})
        advanceStep()
    }

    const handleSkipWithGems = (buildingId: string) => {
        // In tutorial, we might just allow it or subtract from the reward gems
        setUpgradeTimer(prev => {
            const next = { ...prev }
            delete next[buildingId]
            return next
        })
        advanceStep()
    }

    const handleAdSkip = () => {
        setShowAd(true)
    }

    const handleAction = () => {
        if (currentStep.reward) {
            setRewardData(currentStep.reward)
            setShowReward(true)
            setTimeout(() => {
                setShowReward(false)
                if (currentStep.reward?.gems) addGems(currentStep.reward.gems)
                if (currentStep.reward?.gold) addGold(currentStep.reward.gold)
                advanceStep()
            }, 2000)
        } else {
            advanceStep()
        }
    }

    const handleStartUpgrade = () => {
        if (selectedBuilding) {
            setUpgradeTimer(prev => ({ ...prev, [selectedBuilding.id]: 300 }))
            setSelectedBuilding(null)
            setStep(1) // Move to skip step
        }
    }

    const getBuildingImage = (id: string, level: number) => {
        const typeMap: Record<string, string> = {
            'townhall': 'rathaus',
            'barracks': 'kaserne',
            'goldmine': 'goldmine',
            'warehouse': 'lager'
        }
        const typePrefix = typeMap[id] || id
        const clampedLevel = Math.min(Math.max(1, level), 5)
        return `/stadt/${typePrefix}${clampedLevel}.png`
    }

    const formatTime = (seconds: number) => {
        const m = Math.floor(seconds / 60)
        const s = seconds % 60
        return `${m}:${s < 10 ? '0' : ''}${s}`
    }

    return (
        <GameLayout noPadding noScroll showHeader={true} showNav={false}>
            <div className="tut">
                {/* --- HEADER (Visual only, no clicks) --- */}
                <div className="city__player-info" style={{ pointerEvents: 'none' }}>
                    <div className="city__player-avatar">
                        <div className="city__emblem-shield" style={{ backgroundColor: empireColor }}>
                            {empireEmblem}
                        </div>
                    </div>
                    <div className="city__player-text">
                        <span className="city__player-name">{username}</span>
                        <span className="city__player-power">{totalPower.toLocaleString()} ⚔️</span>
                    </div>
                </div>

                {/* --- BUILDING MAP --- */}
                <div className="tut__map">
                    {buildings.map(building => {
                        const isUpgrading = upgradeTimer[building.id] !== undefined
                        const timeRemaining = upgradeTimer[building.id] || 0
                        const isHighlighted = currentStep.highlight === building.id

                        const cssClassMap: Record<string, string> = {
                            'townhall': 'tut__building--townhall',
                            'barracks': 'tut__building--barracks',
                            'goldmine': 'tut__building--goldmine',
                            'warehouse': 'tut__building--warehouse'
                        }

                        return (
                            <div
                                key={building.id}
                                className={`tut__building ${cssClassMap[building.id]} ${isHighlighted ? 'tut__highlight' : ''}`}
                                onClick={() => handleBuildingClick(building)}
                            >
                                {isHighlighted && <div className="tut__arrow">👇</div>}
                                {isUpgrading && (
                                    <div className="tut__building-progress-container" onClick={e => e.stopPropagation()}>
                                        <div className="city__building-progress-bar">
                                            <div
                                                className="city__building-progress-fill"
                                                style={{ width: `${Math.min(100, (1 - timeRemaining / 300) * 100)}%` }}
                                            />
                                        </div>
                                        <div className="city__building-timer">
                                            ⏳ {formatTime(timeRemaining)}
                                        </div>
                                        <div className="city__building-quick-actions">
                                            <button className="city__quick-btn city__quick-btn--gems" onClick={() => handleSkipWithGems(building.id)}>
                                                💎 50
                                            </button>
                                            <button className="city__quick-btn city__quick-btn--ad" onClick={() => handleAdSkip()}>
                                                📺 AD
                                            </button>
                                        </div>
                                    </div>
                                )}
                                <img
                                    src={getBuildingImage(building.id, building.level)}
                                    alt={building.name}
                                    className="tut__building-img"
                                />
                                <div className="tut__building-tag">
                                    <span>Lvl {building.level}</span>
                                    <span>{building.emoji}</span>
                                </div>
                            </div>
                        )
                    })}
                </div>

                {/* --- TUTORIAL OVERLAY --- */}
                <div className="tut__overlay">
                    <div className="tut__card">
                        <h2 className="tut__title">{currentStep.title}</h2>
                        <p className="tut__text">{currentStep.text}</p>

                        {currentStep.skipFree && (
                            <button className="tut__btn tut__btn--main" onClick={handleFreeSkip}>
                                ⚡ GRATIS überspringen!
                            </button>
                        )}

                        {currentStep.reward && (
                            <div className="tut__reward-container">
                                {currentStep.reward.gems && (
                                    <div className="tut__reward-item tut__reward-item--gems">
                                        <span className="tut__reward-emoji">💎</span>
                                        <div className="tut__reward-details">
                                            <span className="tut__reward-value">+{currentStep.reward.gems}</span>
                                            <span className="tut__reward-label">GEMS</span>
                                        </div>
                                    </div>
                                )}
                                {currentStep.reward.gold && (
                                    <div className="tut__reward-item tut__reward-item--gold">
                                        <span className="tut__reward-emoji">💰</span>
                                        <div className="tut__reward-details">
                                            <span className="tut__reward-value">+{currentStep.reward.gold}</span>
                                            <span className="tut__reward-label">GOLD</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {!currentStep.skipFree && currentStep.button && (
                            <button className="tut__btn tut__btn--main" onClick={handleAction}>
                                {currentStep.button}
                            </button>
                        )}
                    </div>

                    <div className="tut__progress">
                        {steps.map((_, i) => (
                            <div key={i} className={`tut__dot ${i < step ? 'tut__dot--done' : i === step ? 'tut__dot--active' : ''}`} />
                        ))}
                    </div>
                </div>

                {/* --- UPGRADE MODAL (Tutorial Version) --- */}
                {selectedBuilding && (
                    <div className="tut__modal-overlay">
                        <div className="tut__modal">
                            <div className="tut__modal-header">
                                <h3 className="tut__modal-title">{selectedBuilding.name} Upgrade</h3>
                            </div>
                            <div className="tut__modal-stats">
                                <div className="tut__stat-card">
                                    <span className="tut__stat-label">Dauer</span>
                                    <span className="tut__stat-value">60s</span>
                                </div>
                                <div className="tut__stat-card">
                                    <span className="tut__stat-label">Kosten</span>
                                    <span className="tut__stat-value">💰 50</span>
                                </div>
                            </div>
                            <button className="tut__btn tut__btn--main" onClick={handleStartUpgrade}>
                                BAU STARTEN
                            </button>
                        </div>
                    </div>
                )}

                {/* --- REWARD POPUP --- */}
                {showReward && rewardData && (
                    <div className="tut__reward-popup">
                        <span className="tut__reward-icon">🎁</span>
                        <div className="tut__reward-items">
                            {rewardData.gems && <span>+{rewardData.gems} 💎</span>}
                            {rewardData.gold && <span>+{rewardData.gold} 💰</span>}
                        </div>
                    </div>
                )}
            </div>

            {/* Ad Modal (Tutorial fallback) */}
            {showAd && (
                <AdModal
                    onComplete={() => { setShowAd(false); handleFreeSkip(); }}
                    onCancel={() => setShowAd(false)}
                />
            )}
        </GameLayout>
    )
}
